import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Speaker } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/download";
import { SIZES } from "@/lib/pkgBrief";
import { YesNo, Chips, Follow, FilePick, fieldCls } from "@/components/pkg/QuizUi";

const SERVICE = ["Service audio e fonica", "Service luci e illuminotecnica"];
const TIERS = [["small", "piccoli eventi"], ["medium", "eventi medi"], ["large", "eventi di grandi dimensioni"]];
export const KIT_EMPTY = { ownAudio: null, audioIndoor: [], audioOutdoor: [], techSheet: null, hasTechSheet: null, ownLights: null, lightsIndoor: [], lightsOutdoor: [], needs: "", budgets: {} };

export const kitRoles = (role, taxonomy = {}) => ({
  artist: role === "artist" || Object.keys(taxonomy.artist || {}).length > 0,
  service: Object.keys(taxonomy.supplier || {}).some((m) => SERVICE.includes(m)),
});

const fileVal = (url) => (url ? { url, name: "Allegato caricato" } : null);
export const kitFromServer = (k = {}) => ({
  ...KIT_EMPTY, ...k, techSheet: fileVal(k.techSheet), hasTechSheet: k.techSheet ? true : k.ownAudio ? false : null,
  budgets: Object.fromEntries(Object.entries(k.budgets || {}).map(([s, t]) => [s, { ...t, price: t.price ?? "", capacity: t.capacity ?? "", details: t.details || "", photo: fileVal(t.photo) }])),
});

const upload = async (v) => {
  if (!v) { return null; }
  if (v.url) { return v.url; }
  const fd = new FormData(); fd.append("file", v.file);
  return (await api.post("/kit-profile/file", fd)).data.url;
};
const num = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

export async function saveKit(k) {
  const budgets = {};
  for (const [s, t] of Object.entries(k.budgets)) {
    if (num(t.price) !== null || num(t.capacity) !== null || t.details?.trim() || t.photo) {
      budgets[s] = { price: num(t.price), capacity: num(t.capacity), details: t.details?.trim() || null, photo: await upload(t.photo) };
    }
  }
  const audio = !!k.ownAudio;
  return (await api.put("/kit-profile", {
    ownAudio: audio, audioIndoor: audio ? k.audioIndoor : [], audioOutdoor: audio ? k.audioOutdoor : [], techSheet: audio && k.hasTechSheet ? await upload(k.techSheet) : null,
    ownLights: !!k.ownLights, lightsIndoor: k.ownLights ? k.lightsIndoor : [], lightsOutdoor: k.ownLights ? k.lightsOutdoor : [], needs: k.needs.trim() || null, budgets,
  })).data;
}

const SizeRows = ({ k, set, prefix, testId }) => (<>
  <Follow label="Per eventi di piccola, media o grande entità AL CHIUSO?"><Chips multi options={SIZES} value={k[`${prefix}Indoor`]} onChange={(v) => set({ [`${prefix}Indoor`]: v })} testId={`${testId}-indoor`}/></Follow>
  <Follow label="Per eventi di piccola, media o grande entità ALL'APERTO?"><Chips multi options={SIZES} value={k[`${prefix}Outdoor`]} onChange={(v) => set({ [`${prefix}Outdoor`]: v })} testId={`${testId}-outdoor`}/></Follow>
</>);

const Tier = ({ s, label, t = {}, onChange, uploadPath }) => {
  const set = (patch) => onChange({ ...t, ...patch });
  return (
    <div className="bg-[#F5F7FB] rounded-2xl p-4 space-y-3" data-testid={`kit-tier-${s}`}>
      <div className="font-semibold text-sm text-aic-navy">Budget service audio/luci base per {label}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-600">Prezzo (€, IVA esclusa)<input type="number" min="0" step="10" value={t.price ?? ""} onChange={(e) => set({ price: e.target.value })} data-testid={`kit-tier-${s}-price`} className={`${fieldCls} mt-1`}/></label>
        <label className="text-xs text-slate-600">Fino a quante persone copre<input type="number" min="0" step="10" value={t.capacity ?? ""} onChange={(e) => set({ capacity: e.target.value })} data-testid={`kit-tier-${s}-capacity`} className={`${fieldCls} mt-1`}/></label>
      </div>
      <textarea rows={2} maxLength={500} value={t.details || ""} onChange={(e) => set({ details: e.target.value })} placeholder="Dettagli sull'allestimento (facoltativo)" data-testid={`kit-tier-${s}-details`} className={fieldCls}/>
      <FilePick value={t.photo || null} onChange={(v) => set({ photo: v })} uploadPath={uploadPath} testId={`kit-tier-${s}-photo`} label="Allega una foto (facoltativo)" imagesOnly/>
    </div>
  );
};

export const KitProfileForm = ({ value: k, onChange, roles, uploadPath }) => {
  const set = (patch) => onChange({ ...k, ...patch });
  return (
    <div className="space-y-8" data-testid="kit-profile-form">
      {roles.artist && <div className="space-y-6" data-testid="kit-artist-section">
        <div><div className="font-semibold text-sm mb-2">Lavori utilizzando attrezzatura audio di tua proprietà?</div><YesNo value={k.ownAudio} onChange={(v) => set({ ownAudio: v })} testId="kit-own-audio"/>
          {k.ownAudio && <><SizeRows k={k} set={set} prefix="audio" testId="kit-audio"/>
            <Follow label="Hai una scheda tecnica?"><YesNo value={k.hasTechSheet} onChange={(v) => set({ hasTechSheet: v })} testId="kit-has-techsheet"/>
              {k.hasTechSheet && <div className="mt-2"><FilePick value={k.techSheet} onChange={(v) => set({ techSheet: v })} uploadPath={uploadPath} testId="kit-techsheet" label="Allega scheda tecnica (documento o foto)"/></div>}</Follow></>}</div>
        <div><div className="font-semibold text-sm mb-2">Lavori utilizzando attrezzatura luci di tua proprietà?</div><YesNo value={k.ownLights} onChange={(v) => set({ ownLights: v })} testId="kit-own-lights"/>
          {k.ownLights && <SizeRows k={k} set={set} prefix="lights" testId="kit-lights"/>}</div>
      </div>}
      {roles.service && <div className="space-y-4" data-testid="kit-service-section">
        <p className="text-sm text-slate-600"><b className="text-aic-navy">Domande facoltative:</b> compilarle migliora il tuo posizionamento nel portale e nelle proposte Evento Chiavi in Mano.</p>
        {TIERS.map(([s, label]) => <Tier key={s} s={s} label={label} t={k.budgets[s]} uploadPath={uploadPath} onChange={(t) => set({ budgets: { ...k.budgets, [s]: t } })}/>)}
      </div>}
      <label className="block text-sm"><span className="font-semibold block mb-2">{roles.service && !roles.artist ? "Hai esigenze particolari (es. accesso con mezzo, spazio minimo richiesto, altro)?" : "Hai esigenze particolari (es. spazio minimo, accesso con mezzo, altro)?"}</span>
        <textarea rows={3} maxLength={1000} value={k.needs} onChange={(e) => set({ needs: e.target.value })} data-testid="kit-needs" className={fieldCls}/></label>
      <div className="flex gap-3 items-start bg-[#FBF6EE] border border-gold/50 rounded-2xl p-4 text-sm text-aic-navy" data-testid="kit-privacy-notice">
        <ShieldCheck className="w-5 h-5 text-gold shrink-0 mt-0.5"/>
        <p>Queste informazioni sono riservate ai pacchetti Chiavi in Mano. Non sono mai visibili ad altri artisti, fornitori o concorrenti. Prima di confermare la disponibilità, verrai contattato direttamente dal committente tramite chat interna o telefonicamente.</p>
      </div>
    </div>
  );
};

export const KitProfileCard = () => {
  const { user } = useAuth();
  const roles = kitRoles(user.role, user.taxonomy);
  const [k, setK] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/kit-profile").then((r) => setK(kitFromServer(r.data))); }, []);
  if (!k || (!roles.artist && !roles.service)) { return null; }
  const save = async () => {
    setBusy(true);
    try { setK(kitFromServer(await saveKit(k))); toast.success("Informazioni per gli eventi salvate"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-8 card-premium p-6" data-testid="kit-profile-card">
      <h3 className="font-display text-xl font-bold flex items-center gap-2 mb-1"><Speaker className="w-5 h-5 text-gold"/>Attrezzatura ed esigenze (Evento Chiavi in Mano)</h3>
      <p className="text-sm text-slate-600 mb-5">Ci aiutano a proporti negli eventi più adatti a te.</p>
      <KitProfileForm value={k} onChange={setK} roles={roles} uploadPath="/kit-profile/file"/>
      <button onClick={save} disabled={busy} data-testid="kit-profile-save" className="btn-pill-gold text-sm mt-5">{busy ? "Salvataggio…" : "Salva"}</button>
    </div>
  );
};
